/**
 * Runtime configuration surface.
 *
 * `/config.js` is rendered by the container entrypoint at startup and loaded
 * before the bundle, so one immutable image serves every environment. When it
 * is absent — npm-lib consumers, unit tests, a stray 404 — every reader falls
 * back to its build-time default, so nothing here is load-bearing for boot.
 *
 * Lives in `src/core` because that is the only directory compiled into both
 * the lib and the SDK builds.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface AqRuntimeConfig {
  /** Console verbosity floor for this environment. Overridden by `?log=` and `__aqLog.setLevel()`. */
  logLevel?: LogLevel;
  /** API server origin the iframe app talks to when the parent sends no `serviceUrl`. */
  apiServerUrl?: string;
  /** Environment name for attribution in the boot log (`dev`, `production`, `local`). */
  env?: string;
  /** Image tag of the running build, so a console screenshot identifies its artifact. */
  build?: string;
}

declare global {
  interface Window {
    __AQ_CONFIG?: AqRuntimeConfig;
  }
}

export function readRuntimeConfig(): AqRuntimeConfig {
  if (typeof window === 'undefined') {
    return {};
  }
  const raw = window.__AQ_CONFIG;
  return raw !== null && typeof raw === 'object' ? raw : {};
}

/** A non-empty string field from the runtime config, or `undefined` to let the caller default. */
export function readRuntimeString(key: 'apiServerUrl' | 'env' | 'build'): string | undefined {
  const value = readRuntimeConfig()[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
